import { LoadingContainer } from '@ff-it/ui';
import { useHasPerm } from 'core/permissions';
import { getProductTypeModule } from 'productTypes';
import { useEntity, useModel } from 'components';
import { useProductAuthoring, authoringProductUrl } from 'modules/supplier/pricelist/ProductAuthoring/adapter';
import { DefaultProductAuthoring } from 'modules/supplier/pricelist/ProductAuthoring/DefaultProductAuthoring';
import type { ProductAuthoringController } from 'modules/supplier/pricelist/ProductAuthoring/controller';
import type {
  ProductAuthoringCatalogActions,
  ProductAuthoringProduct,
} from 'modules/supplier/pricelist/ProductAuthoring/types';
import type { PriceList } from 'modules/supplier/pricelist/types';
import { ReactElement, Suspense } from 'react';

export function Prices(): ReactElement {
  const { permissions } = useModel();
  const {
    endpoint,
    item: { type },
  } = useEntity<PriceList>();
  const hasUpdate = useHasPerm(permissions?.change);
  const typeModule = getProductTypeModule(type);
  const Workspace = typeModule?.workspace;
  const authoring = useProductAuthoring<ProductAuthoringProduct>(endpoint);

  if (authoring.error) throw authoring.error;

  const controller = authoring.input
    ? ({ input: authoring.input, actions: authoring.actions } satisfies ProductAuthoringController)
    : null;

  // Catalogue actions (move/convert/remove) and copy live in the drawer; MOVE
  // stays in this workspace and reconciles instead of navigating away.
  const vendorId = authoring.input?.priceList.vendor_id;
  const catalogActions: ProductAuthoringCatalogActions<ProductAuthoringProduct> | undefined =
    vendorId !== undefined && controller
      ? {
          productEndpoint: (product) => authoringProductUrl(vendorId, product),
          onChanged: () => void authoring.mutate(),
        }
      : undefined;

  return (
    <LoadingContainer loading={authoring.isLoading}>
      {controller &&
        (Workspace ? (
          <Suspense fallback={null}>
            <Workspace
              controller={controller}
              priceListEndpoint={endpoint}
              canEdit={hasUpdate}
              catalogActions={catalogActions}
            />
          </Suspense>
        ) : (
          <DefaultProductAuthoring
            controller={controller}
            formConfig={typeModule?.formConfig}
            canEdit={hasUpdate}
            listPriceEditable={type !== 'PRESS'}
            catalogActions={catalogActions}
          />
        ))}
    </LoadingContainer>
  );
}
